import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'

const STATES = [
  { label: 'Needs review', colour: RAG_LEVELS.critical.fg, text: 'you haven’t decided whether this obligation applies to your building yet. Confirm it applies, or mark it ‘Not applicable’. You don’t need to upload a document to finish this. That’s the compliance score, not setup.' },
  { label: 'Applies', colour: RAG_LEVELS.low.fg, text: 'you’ve confirmed this obligation applies. It now counts towards your compliance score and is satisfied by an up-to-date document or a completed task (which you can add any time).' },
  { label: 'Not applicable', colour: RAG_LEVELS.none.fg, text: 'you’ve decided this obligation doesn’t apply to this building (e.g. no gas supply, so no CP12 needed). ‘Not applicable’ obligations are excluded from the compliance score.' },
]

const ComplianceSetupExplainer = ({ align = 'right', label = 'What does setup mean?' }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const [coords, setCoords] = useState(null)
  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const margin = 12
      const popWidth = Math.min(360, window.innerWidth - 2 * margin)
      const desiredLeft = align === 'left' ? r.left : r.right - popWidth
      const left = Math.max(margin, Math.min(desiredLeft, window.innerWidth - popWidth - margin))
      setCoords({ top: r.bottom + 8, left })
    }
  }, [open, align])

  return (
    <Wrap>
      <InfoButton
        ref={btnRef}
        type='button'
        aria-label='What does compliance setup mean?'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <InfoOutlinedIcon style={{ fontSize: 15 }} />
        <span>{label}</span>
      </InfoButton>

      {open && coords && createPortal(
        <Pop
          ref={popRef}
          role='dialog'
          aria-label='What compliance setup means'
          style={{ top: coords.top, left: coords.left }}
        >
          <PopTitle>Compliance setup: what it means</PopTitle>
          <PopLead>
            Obligations are automatically identified per building (you can add more manually).
            Setup is simply <strong>deciding what applies</strong>: for each obligation you confirm
            it applies to your building, or mark it not applicable. It’s separate from the
            compliance <strong>score</strong>, which measures how much of what applies is actually
            satisfied and up-to-date.
          </PopLead>

          <SectionTitle>Every obligation is in one of three states</SectionTitle>
          <StatusList>
            {STATES.map(s => (
              <StatusRow key={s.label}>
                <Dot $colour={s.colour} />
                <StatusText><strong>{s.label}</strong>: {s.text}</StatusText>
              </StatusRow>
            ))}
          </StatusList>

          <SectionTitle>How you finish</SectionTitle>
          <PopLead>
            Review each obligation once. Confirm it applies, or mark it not applicable. You’re
            finished when nothing is left in <strong>“Needs review”</strong>. Uploading documents
            and scheduling tasks is optional here; that keeps your compliance <strong>score</strong>
            up, and can be done any time after.
          </PopLead>

          <FootNote>
            Compliance Setup and Compliance Score are not the same thing. Setup means you have a
            plan in place. Your compliance score tells you whether that plan is working. A
            building might have 100% Setup, but score 0 on the Compliance Score. This means that
            none of the applicable obligations have been satisfied.
          </FootNote>
        </Pop>,
        document.body
      )}
    </Wrap>
  )
}

export default ComplianceSetupExplainer

const Wrap = styled.span`
  display: inline-flex;
`

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Pop = styled.div`
  position: fixed;
  z-index: 100050;
  width: 360px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 18px 44px rgba(12, 18, 41, 0.22);
`

const PopTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const PopLead = styled.p`
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4d4d56;
`

const SectionTitle = styled.h5`
  margin: 0 0 8px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const StatusList = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  margin-bottom: 16px;
`

const StatusRow = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
`

const Dot = styled.span`
  flex: 0 0 auto;
  width: 9px; height: 9px; margin-top: 4px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  box-shadow: 0 0 0 2px ${({ $colour }) => `${$colour}33`};
`

const StatusText = styled.span`
  font-size: 12px; line-height: 1.45; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const FootNote = styled.p`
  margin: 0;
  font-size: 11.5px;
  line-height: 1.45;
  font-style: italic;
  color: #6e6e78;
`
